import { forwardRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDemo } from '@/contexts/DemoContext';
import { DemoConversionCta } from './DemoConversionCta';
import { calculateDaysRemaining } from '@/lib/utils';

export const DemoLeaderboardSection = forwardRef<HTMLElement>(
  function DemoLeaderboardSection(_, ref) {
    const { t } = useLanguage();
    const { mockUsers, mockGroup } = useDemo();

    const daysRemaining = calculateDaysRemaining(mockGroup.challenge_end);

    // Assign ranks (competition ranking)
    let currentRank = 0;
    let previousDrinks = -1;
    const rankedUsers = mockUsers.map((user, index) => {
      if (user.totalDrinks !== previousDrinks) {
        currentRank = index + 1;
        previousDrinks = user.totalDrinks;
      }
      return { ...user, rank: currentRank };
    });

    return (
      <section ref={ref} id="leaderboard" className="py-8 border-t border-border">
        {/* Section Header */}
        <div className="px-6 pb-4 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-serif font-bold mb-2">{t('leaderboardTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {daysRemaining} {t('leaderboardDaysRemaining')}
          </p>
        </div>

        {/* Leaderboard Table */}
        <div className="px-6 max-w-lg mx-auto">
          <div className="border border-border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto_auto] gap-2 px-3 py-2 bg-muted/50 text-xs text-muted-foreground font-medium">
              <div className="w-6 text-center">#</div>
              <div>{t('leaderboardUser')}</div>
              <div className="w-10 text-center">{t('leaderboardTotal')}</div>
              <div className="w-8 text-center">🗓️</div>
              <div className="w-8 text-center">🍺</div>
              <div className="w-8 text-center">🍷</div>
              <div className="w-8 text-center">🥃</div>
            </div>

            {/* Rows */}
            {rankedUsers.map((user) => {
              const isDemo = user.user_id.includes('demo');
              return (
                <div 
                  key={user.id}
                  className={`
                    grid grid-cols-[auto_1fr_auto_auto_auto_auto_auto] gap-2 px-3 py-2.5 border-t border-border
                    ${isDemo ? 'bg-primary/5' : ''}
                  `}
                >
                  <div className="w-6 text-center text-sm font-medium text-muted-foreground">
                    {user.rank}
                  </div>
                  <div className={`text-sm truncate ${isDemo ? 'font-semibold text-primary' : ''}`}>
                    {user.user_id}
                  </div>
                  <div className="w-10 text-center text-sm font-mono font-semibold">
                    {user.totalDrinks}
                  </div>
                  <div className="w-8 text-center text-sm font-mono text-muted-foreground">
                    {user.longestStreak}
                  </div>
                  <div className="w-8 text-center text-sm font-mono text-muted-foreground">
                    {user.beer}
                  </div>
                  <div className="w-8 text-center text-sm font-mono text-muted-foreground">
                    {user.wine}
                  </div>
                  <div className="w-8 text-center text-sm font-mono text-muted-foreground">
                    {user.shot}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Conversion CTA */}
        <DemoConversionCta variant="inline" className="mt-6" />
      </section>
    );
  }
);
